import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, UserCog, Users2, Check, History, AlertTriangle, Trash2, PhoneCall, Send, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { SortableTh, useSort, sortRows } from "../components/SortableTh";
import { usePaged, Paginator } from "../components/Paginator";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const ROLES = [
  { value: "member", label: "Member" },
  { value: "leader", label: "Staff (Leader)" },
  { value: "pastor", label: "Pastor" },
  { value: "admin", label: "Portal Admin" },
];
const ROLE_STYLE = {
  admin: { bg: "#17A88A1A", color: "#17A88A" },
  pastor: { bg: "#0B5E4E1A", color: "#0B5E4E" },
  leader: { bg: "#0844381A", color: "#084438" },
  team_leader: { bg: "#A8A29E1A", color: "#14211F" },
  member: { bg: "#EBE9E4", color: "#14211F" },
};
const CAP_TEAMS = ["Pastoral", "Follow-up", "Finance"];

const capLabel = (caps) => {
  const out = [];
  if (caps?.manage_members) out.push("Members");
  if (caps?.call_members) out.push("Call");
  if (caps?.pastoral_care) out.push("Pastoral");
  if (caps?.view_prayers) out.push("Prayers");
  if (caps?.view_donations) out.push("Giving");
  if (caps?.manage_content) out.push("Content");
  if (caps?.evangelism) out.push("Evangelism");
  if (caps?.systems_admin) out.push("Sys Admin");
  return out;
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selTeams, setSelTeams] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [audit, setAudit] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [handoverWeeks, setHandoverWeeks] = useState(3);
  const [savingCare, setSavingCare] = useState(false);
  const [autoCheckin, setAutoCheckin] = useState({ enabled: false, channel: "sms", message: "" });
  const [savingAuto, setSavingAuto] = useState(false);

  const load = async () => {
    try {
      const [u, t, a, c] = await Promise.all([api.get("/users"), api.get("/teams"), api.get("/audit"), api.get("/settings/care")]);
      setUsers(u.data);
      setTeams(t.data);
      setAudit(a.data);
      setHandoverWeeks(c.data.pastoral_handover_weeks);
      if (c.data.auto_checkin) setAutoCheckin(c.data.auto_checkin);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const saveHandover = async (weeks) => {
    setSavingCare(true);
    setHandoverWeeks(weeks);
    try {
      await api.put("/settings/care", { pastoral_handover_weeks: weeks });
      toast.success(`New members now stay in Pastoral care for ${weeks} weeks before follow-up.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
    finally { setSavingCare(false); }
  };

  const saveAutoCheckin = async (next) => {
    const merged = { ...autoCheckin, ...next };
    setAutoCheckin(merged);
    setSavingAuto(true);
    try {
      await api.put("/settings/care", { auto_checkin: merged });
      toast.success(merged.enabled ? "Weekly auto check-in is ON." : "Weekly auto check-in is OFF.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
    finally { setSavingAuto(false); }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/care-digest/send");
      const n = data.recipients || 0;
      const od = data.stats?.overdue ?? 0;
      if (data.status === "simulated") toast.warning(`Digest simulated for ${n} leader(s) — ${od} overdue (add Resend key to send for real).`);
      else toast.success(`Weekly digest sent to ${n} leader(s) · ${od} overdue member(s) flagged.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSendingDigest(false); }
  };

  const changeRole = async (id, role) => {
    setSaving(id);
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const openTeams = (u) => { setEditing(u); setSelTeams(u.team_ids || []); };
  const toggleTeam = (id) => setSelTeams((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const [capsFor, setCapsFor] = useState(null);
  const [capForm, setCapForm] = useState({ manage_members: false, pastoral_care: false, view_donations: false, evangelism: false, visitor_care: false, view_prayers: false, coordinator: false });
  const [savingCaps, setSavingCaps] = useState(false);
  const openCaps = (u) => {
    const o = u.cap_overrides || {};
    setCapForm({ manage_members: !!o.manage_members, pastoral_care: !!o.pastoral_care, view_donations: !!o.view_donations, evangelism: !!o.evangelism, visitor_care: !!o.visitor_care, view_prayers: !!o.view_prayers, coordinator: !!o.coordinator });
    setCapsFor(u);
  };
  const saveCaps = async () => {
    setSavingCaps(true);
    try {
      await api.put(`/users/${capsFor.id}/capabilities`, capForm);
      toast.success("Permissions updated");
      setCapsFor(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCaps(false); }
  };

  const saveTeams = async () => {
    setSaving(editing.id);
    try {
      await api.put(`/users/${editing.id}/teams`, { team_ids: selTeams });
      toast.success("Teams updated — access recalculated");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "";

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: resetConfirm });
      toast.success(`Cleared ${data.records_cleared} records and ${data.users_removed} accounts. Fresh start ready.`);
      setResetOpen(false); setResetConfirm("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  const matchFilter = (u) => {
    const c = u.caps || {};
    switch (filter) {
      case "members": return c.manage_members;
      case "giving": return c.view_donations;
      case "content": return c.manage_content;
      case "admin": return u.role === "admin";
      case "pastor": return u.role === "pastor";
      case "none": return !c.manage_members && !c.view_donations && !c.manage_content && u.role !== "admin" && u.role !== "pastor";
      default: return true;
    }
  };
  const q = query.trim().toLowerCase();
  const shown = users.filter((u) =>
    matchFilter(u) && (!q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)));
  const { sortKey, sortDir, onSort } = useSort();
  const sortedShown = sortRows(shown, sortKey, sortDir, {
    name: (u) => u.name,
    email: (u) => u.email || u.phone,
    role: (u) => u.role,
    lastlogin: (u) => u.last_login || "",
  });
  const usersPaged = usePaged(sortedShown, 25);

  const FILTERS = [
    { value: "all", label: "All users" },
    { value: "members", label: "Can manage members" },
    { value: "giving", label: "Can view giving" },
    { value: "content", label: "Can manage content" },
    { value: "admin", label: "Portal admins" },
    { value: "pastor", label: "Pastors" },
    { value: "none", label: "No special access" },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Users & Access</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Assign Pastors and Portal Admins. Team membership grants extra access — Pastoral/Follow-up can manage members, Finance can view giving.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="users-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email…"
          className="flex-1 min-w-[220px] bg-white border border-[#EBE9E4] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20" />
        <select data-testid="users-filter-select" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="bg-white border border-[#EBE9E4] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20">
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <span className="text-sm" style={{ color: "var(--sj-text-3)" }} data-testid="users-count">{shown.length} of {users.length}</span>
      </div>

      <div className={`${CARD} p-2 sm:p-4`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left" style={{ color: "var(--sj-text-3)" }}>
                <SortableTh label="Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-users-name" className="py-3 px-4" />
                <SortableTh label="Email" sortKey="email" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-users-email" className="py-3 px-4" />
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Access</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Teams</th>
                <SortableTh label="Role" sortKey="role" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-users-role" className="py-3 px-4" />
                <SortableTh label="Last logged in" sortKey="lastlogin" activeKey={sortKey} dir={sortDir} onSort={onSort} testid="sort-users-lastlogin" className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {usersPaged.pageItems.map((u) => {
                const rs = ROLE_STYLE[u.role] || ROLE_STYLE.member;
                const caps = capLabel(u.caps);
                const capTeams = (u.team_ids || []).map(teamName).filter((n) => CAP_TEAMS.includes(n));
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)" }}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 font-medium">
                        {u.role === "admin" && <ShieldCheck className="h-4 w-4" style={{ color: "#17A88A" }} />}
                        {u.name}
                      </div>
                    </td>
                    <td className="py-3 px-4" style={{ color: "var(--sj-text-2)" }}>{u.email ? u.email : (u.phone ? <span className="inline-flex items-center gap-1.5">{u.phone}<span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#17A88A1A", color: "#17A88A" }}>mobile login</span></span> : <span className="italic text-xs px-2 py-0.5 rounded-full" style={{ background: "#0844381A", color: "#084438" }}>No portal login</span>)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap items-center gap-1">
                        {(u.role === "admin" || u.role === "pastor") ? (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#17A88A1A", color: "#17A88A" }}>Full access</span>
                        ) : (
                          <>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" data-testid={`effective-role-${u.id}`} style={{ background: "#08443814", color: "#084438" }}>{u.effective_role_label || "Team Member"}</span>
                            {caps.length === 0 ? <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>no permissions</span> :
                              caps.map((c) => <span key={c} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--sj-peach)", color: "var(--sj-primary)" }}>{c}</span>)}
                            <button data-testid={`edit-caps-${u.id}`} onClick={() => openCaps(u)} title="Edit permissions" className="p-1 rounded-lg hover:bg-[#E3F1EC] transition-colors" style={{ color: "var(--sj-secondary)" }}>
                              <SlidersHorizontal className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <button data-testid={`user-teams-button-${u.id}`} onClick={() => openTeams(u)}
                        className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                        <Users2 className="h-3.5 w-3.5" />
                        {capTeams.length ? capTeams.join(", ") : "Assign teams"}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      {u.role === "team_leader" ? (
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: rs.bg, color: rs.color }}>Team Leader</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <UserCog className="h-4 w-4" style={{ color: "var(--sj-text-3)" }} />
                          <select
                            data-testid={`user-role-select-${u.id}`}
                            disabled={saving === u.id}
                            value={u.role}
                            onChange={(e) => changeRole(u.id, e.target.value)}
                            className="bg-[#EDF2F4] border border-[#EBE9E4] rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20 disabled:opacity-60">
                            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4" data-testid={`user-lastlogin-${u.id}`} style={{ color: u.last_login ? "var(--sj-text-2)" : "var(--sj-text-3)" }}>
                      {u.last_login ? new Date(u.last_login).toLocaleDateString('en-GB', { day: "numeric", month: "short", year: "numeric" }) : "Never"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Paginator page={usersPaged.page} pageCount={usersPaged.pageCount} onPage={usersPaged.setPage} testid="users-paginator" />
      </div>

      <div className={`${CARD} p-6`} data-testid="audit-log">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
          <h3 className="font-head text-xl font-semibold">Audit trail</h3>
          <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>· access changes &amp; prayer request activity</span>
          <button data-testid="purge-prayer-alerts-btn" onClick={async () => {
            try {
              const { data } = await api.post("/admin/cleanup-prayer-notifications");
              toast.success(data.removed > 0 ? `Purged ${data.removed} historic prayer alert(s) from non-pastoral accounts.` : "No stray prayer alerts found — everything's clean.");
            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
          }} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
            <ShieldCheck className="h-3.5 w-3.5" /> Purge stray prayer alerts
          </button>
        </div>
        {audit.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No activity recorded yet.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {audit.map((a) => {
              const AUDIT_LABEL = { role_change: "Role", profile_update: "Profile", team_change: "Team", prayer_view: "Prayer view", prayer_status: "Prayer", prayer_delete: "Prayer", prayer_cleanup: "Cleanup", privacy_consent: "Consent" };
              const isPrayer = (a.action || "").startsWith("prayer");
              return (
              <div key={a.id} className="flex items-start gap-3 text-sm py-2 border-b last:border-b-0" style={{ borderColor: "var(--sj-border)" }}>
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mt-0.5 shrink-0" style={{ background: isPrayer ? "#B4530915" : a.action === "role_change" ? "#0B5E4E1A" : "#17A88A1A", color: isPrayer ? "#B45309" : a.action === "role_change" ? "#0B5E4E" : "#17A88A" }}>
                  {AUDIT_LABEL[a.action] || "Change"}
                </span>
                <div className="min-w-0">
                  <div><span className="font-semibold">{a.target_name}</span> — {a.detail}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>by {a.actor_name} · {new Date(a.created_at).toLocaleString('en-GB', { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Teams for {editing?.name}</DialogTitle></DialogHeader>
          <p className="text-sm -mt-1" style={{ color: "var(--sj-text-2)" }}>Pastoral / Follow-up grant member management. Finance grants access to giving records.</p>
          <div className="space-y-2 mt-3 max-h-[50vh] overflow-y-auto" data-testid="teams-checklist">
            {teams.map((t) => {
              const on = selTeams.includes(t.id);
              const highlight = CAP_TEAMS.includes(t.name);
              return (
                <button key={t.id} type="button" data-testid={`team-toggle-${t.id}`} onClick={() => toggleTeam(t.id)}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors"
                  style={{ borderColor: on ? "var(--sj-primary)" : "var(--sj-border)", background: on ? "var(--sj-peach)" : "#fff" }}>
                  <div>
                    <div className="font-medium text-sm flex items-center gap-2">
                      {t.name}
                      {highlight && <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#17A88A1A", color: "#17A88A" }}>grants access</span>}
                    </div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{t.description}</div>
                  </div>
                  <span className="h-5 w-5 rounded-md flex items-center justify-center shrink-0 border" style={{ borderColor: on ? "var(--sj-primary)" : "var(--sj-border)", background: on ? "var(--sj-primary)" : "#fff" }}>
                    {on && <Check className="h-3.5 w-3.5 text-white" />}
                  </span>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="save-teams-button" disabled={saving === editing?.id} onClick={saveTeams} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>Save Teams</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!capsFor} onOpenChange={(o) => !o && setCapsFor(null)}>
        <DialogContent data-testid="caps-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Permissions for {capsFor?.name}</DialogTitle></DialogHeader>
          <p className="text-sm -mt-1" style={{ color: "var(--sj-text-2)" }}>Grant this person specific abilities directly — no need to add them to a team. These add to any access they already get from their teams.</p>
          <div className="space-y-2 mt-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="caps-checklist">
            {[
              { key: "manage_members", label: "Manage members", desc: "Add, edit, delete members; send login details; import/export." },
              { key: "pastoral_care", label: "Pastoral care & follow-up", desc: "Log calls, run check-ins, manage the care pipeline." },
              { key: "view_donations", label: "Giving / Finance", desc: "View and manage donation records." },
              { key: "evangelism", label: "Evangelism outreach", desc: "Add & manage evangelism outreach contacts. Cannot see members." },
              { key: "visitor_care", label: "Visitor care", desc: "Add & manage church visitors, call them and hand off to pastoral. Cannot see members." },
              { key: "view_prayers", label: "Prayer requests", desc: "View and manage members' prayer requests. Restricted to pastors & the Pastoral team." },
              { key: "coordinator", label: "Course coordinator", desc: "Manage courses, class registers, attendance & learners. Can add/register learners onto courses." },
            ].map((c) => {
              const on = capForm[c.key];
              return (
                <button key={c.key} type="button" data-testid={`cap-toggle-${c.key}`} onClick={() => setCapForm({ ...capForm, [c.key]: !on })}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors"
                  style={{ borderColor: on ? "var(--sj-primary)" : "var(--sj-border)", background: on ? "var(--sj-peach)" : "#fff" }}>
                  <div>
                    <div className="font-medium text-sm">{c.label}</div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{c.desc}</div>
                  </div>
                  <span className="h-5 w-5 rounded-md flex items-center justify-center shrink-0 border" style={{ borderColor: on ? "var(--sj-primary)" : "var(--sj-border)", background: on ? "var(--sj-primary)" : "#fff" }}>
                    {on && <Check className="h-3.5 w-3.5 text-white" />}
                  </span>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setCapsFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="save-caps-button" disabled={savingCaps} onClick={saveCaps} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{savingCaps ? "Saving…" : "Save Permissions"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="rounded-2xl border p-6" style={{ borderColor: "var(--sj-border)", background: "#fff" }} data-testid="care-settings">
        <div className="flex items-center gap-2 mb-1">
          <PhoneCall className="h-5 w-5" style={{ color: "var(--sj-secondary)" }} />
          <h3 className="font-head text-xl font-semibold" style={{ color: "var(--sj-text)" }}>Pastoral care settings</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>
          Choose how long a new member stays with the <strong>Pastoral</strong> team before they're automatically handed over to <strong>Follow-Up</strong>. (Members also move over automatically once 2 pastoral calls are logged, or when handed off manually.)
        </p>
        <div className="flex items-center gap-2">
          {[2, 3, 4].map((w) => (
            <button
              key={w}
              data-testid={`handover-weeks-${w}`}
              disabled={savingCare}
              onClick={() => saveHandover(w)}
              className="rounded-full px-5 py-2.5 text-sm font-semibold border transition-all disabled:opacity-60"
              style={{
                borderColor: handoverWeeks === w ? "var(--sj-secondary)" : "var(--sj-border)",
                background: handoverWeeks === w ? "var(--sj-secondary)" : "#fff",
                color: handoverWeeks === w ? "#fff" : "var(--sj-text-2)",
              }}
            >
              {w} weeks
            </button>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--sj-border)" }} data-testid="auto-checkin-settings">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-head text-base font-bold" style={{ color: "var(--sj-text)" }}>Automatic weekly check-in</div>
              <p className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>
                Once a week, automatically message anyone overdue (7+ days without contact) so no one is forgotten.
              </p>
            </div>
            <button
              data-testid="auto-checkin-toggle"
              disabled={savingAuto}
              onClick={() => saveAutoCheckin({ enabled: !autoCheckin.enabled })}
              className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60"
              style={{ background: autoCheckin.enabled ? "var(--sj-secondary)" : "var(--sj-border)" }}
              aria-label="Toggle auto check-in"
            >
              <span className="inline-block h-5 w-5 rounded-full bg-white shadow transition-transform" style={{ transform: autoCheckin.enabled ? "translateX(22px)" : "translateX(3px)" }} />
            </button>
          </div>
          {autoCheckin.enabled && (
            <div className="mt-4 space-y-3">
              <div className="flex gap-2 max-w-xs">
                {[["sms", "Text (SMS)"], ["email", "Email"]].map(([v, l]) => (
                  <button key={v} data-testid={`auto-channel-${v}`} disabled={savingAuto} onClick={() => saveAutoCheckin({ channel: v })}
                    className="flex-1 rounded-xl py-2 text-sm font-medium border transition-all disabled:opacity-60"
                    style={autoCheckin.channel === v ? { background: "var(--sj-secondary)", color: "#fff", borderColor: "var(--sj-secondary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                    {l}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Message (use {"{first}"} for their name)</label>
                <textarea data-testid="auto-checkin-message" rows={3} value={autoCheckin.message}
                  onChange={(e) => setAutoCheckin({ ...autoCheckin, message: e.target.value })}
                  onBlur={() => saveAutoCheckin({})}
                  className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#17A88A]/20 focus:border-[#17A88A] outline-none transition-all px-4 py-2.5 text-sm" />
              </div>
              <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>
                Messages send for real once Twilio/Resend keys are added — until then they're safely simulated.
              </p>
            </div>
          )}

          <div className="mt-5 pt-5 border-t flex items-center justify-between gap-4 flex-wrap" style={{ borderColor: "var(--sj-border)" }} data-testid="weekly-digest-row">
            <div>
              <div className="font-head text-base font-bold" style={{ color: "var(--sj-text)" }}>Weekly overdue digest</div>
              <p className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>
                Every week, Pastoral &amp; Follow-up leaders (and pastors/admins) get an email summarising the care queue and who's overdue for a call. Send one now to preview it.
              </p>
            </div>
            <button data-testid="send-digest-now" disabled={sendingDigest} onClick={sendDigestNow}
              className="shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-secondary)" }}>
              <Send className="h-4 w-4" /> {sendingDigest ? "Sending…" : "Send digest now"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border p-6" style={{ borderColor: "#0B5E4E55", background: "#0B5E4E0A" }} data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
          <h3 className="font-head text-xl font-semibold" style={{ color: "var(--sj-primary)" }}>Danger zone</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--sj-text-2)" }}>
          <strong>Reset data for a fresh start.</strong> This permanently deletes all members, courses, giving records, messages, prayer requests, events and every non-admin login. Admin accounts and the standard teams are kept. Use this once to clear sample/demo data before going live. <strong>This cannot be undone.</strong>
        </p>
        <button data-testid="open-reset-button" onClick={() => { setResetConfirm(""); setResetOpen(true); }}
          className="flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold border transition-all hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}>
          <Trash2 className="h-4 w-4" /> Reset all data
        </button>
      </div>

      <Dialog open={resetOpen} onOpenChange={(o) => !o && setResetOpen(false)}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "#0B5E4E1A" }}>
                <AlertTriangle className="h-6 w-6" style={{ color: "var(--sj-primary)" }} />
              </div>
              <DialogTitle className="font-head text-2xl">Reset all data?</DialogTitle>
            </div>
          </DialogHeader>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>
            This will permanently delete <strong>all members, non-admin logins, courses, giving, messages, prayer requests and events</strong>. Your admin account and the standard teams stay. This action <strong>cannot be undone</strong>.
          </p>
          <div className="mt-3">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Type <span style={{ color: "var(--sj-primary)" }}>RESET</span> to confirm</label>
            <input data-testid="reset-confirm-input" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="RESET"
              className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm" />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setResetOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="confirm-reset-button" disabled={resetConfirm !== "RESET" || resetting} onClick={runReset}
              className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>
              {resetting ? "Resetting..." : "Yes, delete everything"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
